import { Trash2 } from 'lucide-react';
import React from 'react';
import type { FieldInfo } from '../../engage-segments-tab/types';
import { AddDescriptionButton } from './AddDescriptionButton';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { acceptsMultipleValues } from './field-type-operators';
import { FieldSelector } from './FieldSelector';
import { OperatorSelector } from './OperatorSelector';
import type { BuilderFieldCondition } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { ValueInput } from './ValueInput';
import { isDateValueCompatibleWithOperator } from './ValueInput/date-value-utils';

interface FieldConditionRowProps {
  condition: BuilderFieldCondition;
  fieldOptions: FieldInfo[];
  onUpdate: (condition: BuilderFieldCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
}

function getValueForOperatorChange(
  condition: BuilderFieldCondition,
  operator: BuilderFieldCondition['operator'],
): BuilderFieldCondition['value'] {
  if (condition.value.length === 0) return condition.value;

  if (condition.fieldType === 'date') {
    const firstValue = condition.value[0];
    return isDateValueCompatibleWithOperator(operator, firstValue) ? condition.value.slice(0, 1) : [];
  }

  // Single-value inputs edit value[0]; keeping the rest would silently widen the compiled OR
  return acceptsMultipleValues(operator) ? condition.value : condition.value.slice(0, 1);
}

export const FieldConditionRow: React.FC<FieldConditionRowProps> = ({
  condition,
  fieldOptions,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const currentFieldInfo = fieldOptions.find(f => f.name === condition.field);

  const handleFieldChange = (field: string) => {
    const option = fieldOptions.find(f => f.name === field);
    const fieldType = option?.type ?? 'unknown';
    onUpdate({
      ...condition,
      field,
      fieldType,
    });
  };

  const handleOperatorChange = (operator: BuilderFieldCondition['operator']) => {
    onUpdate({
      ...condition,
      operator,
      value: getValueForOperatorChange(condition, operator),
    });
  };

  const handleValueChange = (value: BuilderFieldCondition['value']) => {
    onUpdate({ ...condition, value });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  return (
    <div className={styles.conditionRowWrapper}>
      {condition.description !== undefined && (
        <div className={styles.rowDescription}>
          <ClauseDescriptionHeadline
            variant="row"
            description={condition.description}
            onChange={handleDescriptionChange}
            disabled={disabled}
          />
        </div>
      )}
      <div className={styles.conditionRow}>
        <div className={styles.conditionRowField}>
          <FieldSelector
            options={fieldOptions}
            value={condition.field}
            onChange={handleFieldChange}
            placeholder="Field"
            disabled={disabled}
          />
        </div>
        <div className={styles.conditionRowOperator}>
          <OperatorSelector
            fieldType={condition.fieldType}
            value={condition.operator}
            onChange={handleOperatorChange}
            disabled={disabled}
          />
        </div>
        <div className={styles.conditionRowValue}>
          <ValueInput
            fieldType={condition.fieldType}
            operator={condition.operator}
            value={condition.value}
            onChange={handleValueChange}
            disabled={disabled}
            enumOptions={currentFieldInfo?.enum}
          />
        </div>
        <div className={styles.conditionRowControls}>
          {condition.description === undefined && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          <button
            type="button"
            className={styles.conditionRowDelete}
            onClick={onDelete}
            disabled={disabled}
            aria-label="Remove condition">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
